
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TranscriptionTimer } from '@/components/TranscriptionTimer';
import { ApiTranscriptionData, TranscriptionData } from '@/types/sections';

interface TranscriptionPanelProps {
  transcriptions?: ApiTranscriptionData[];
  showHeader?: boolean;
  showFeedback?: boolean;
  autoScroll?: boolean;
  isRecording?: boolean;
  onTimeUpdate?: (formattedTime: string, totalSeconds: number) => void;
  shouldResetTimer?: boolean;
  isFinished?: boolean; // Nueva prop
}

// Function to generate a unique key for each transcription
const generateUniqueKey = (item: TranscriptionData, fallbackIndex: number): string => {
  // Create a unique key based on timestamp, speaker, and first 20 characters of text
  const textPrefix = item.text.substring(0, 20).replace(/\s+/g, '_');
  const key = `${item.timestamp}-${item.speaker}-${textPrefix}-${fallbackIndex}`;
  return key;
};

// Function to get speaker color based on speaker type or ID
const getSpeakerColor = (speaker: string): string => {
  const speakerColors = {
    'Doctor': 'bg-teal-500',
    'Paciente': 'bg-blue-500',
    'Patient': 'bg-blue-500',
    'Speaker 1': 'bg-green-500',
    'Speaker 2': 'bg-purple-500',
    'Speaker 3': 'bg-orange-500',
    'Speaker 4': 'bg-pink-500',
    'Speaker A': 'bg-indigo-500',
    'Speaker B': 'bg-red-500',
    'Hablante 0': 'bg-teal-500', // Mantener el color original para Hablante 0
    'Hablante 1': 'bg-green-500',
    'Hablante 2': 'bg-purple-500',
  };

  // Return specific color if found, otherwise generate a color based on speaker string hash
  if (speakerColors[speaker as keyof typeof speakerColors]) {
    return speakerColors[speaker as keyof typeof speakerColors];
  }

  // Generate a consistent color based on speaker string hash
  const colors = ['bg-teal-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-red-500'];
  let hash = 0;
  for (let i = 0; i < speaker.length; i++) {
    hash = speaker.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const TranscriptionPanel = ({ 
  transcriptions = [], 
  showHeader = true,
  showFeedback = true,
  autoScroll = true,
  isRecording = false,
  onTimeUpdate,
  shouldResetTimer = false,
  isFinished = false
}: TranscriptionPanelProps) => {
  const { t } = useTranslation();
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Convert API format to display format
  const convertTranscriptions = (apiTranscriptions: ApiTranscriptionData[]): TranscriptionData[] => {
    return apiTranscriptions.map(item => ({
      text: item.transcript,
      speaker: item.speakerType || item.speakerId,
      timestamp: item.recordTime
    }));
  };

  // Check if we have real transcriptions
  const hasTranscriptions = transcriptions && transcriptions.length > 0;
  const displayTranscriptions = hasTranscriptions ? convertTranscriptions(transcriptions) : [];

  // Simple and reliable auto-scroll effect
  useEffect(() => {
    if (autoScroll && hasTranscriptions && scrollAreaRef.current) {
      console.log('🔄 Auto-scrolling to bottom...');
      
      const scrollToBottom = () => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;

        // Find the viewport within the ScrollArea
        const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight;
          console.log('✅ Scrolled using viewport');
        }
      };

      // Immediate scroll
      scrollToBottom();
      
      // Additional scroll after a short delay to ensure content is rendered
      const timeoutId = setTimeout(scrollToBottom, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [transcriptions.length, autoScroll, hasTranscriptions]);

  return (
    <div className="h-full flex flex-col">
      {showHeader && (
        <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold" style={{ color: '#194d4b' }}>{t('transcription')}</h3>
            <TranscriptionTimer 
              isRecording={isRecording} 
              onTimeUpdate={onTimeUpdate}
              shouldReset={shouldResetTimer}
              isFinished={isFinished}
            />
          </div>
        </div>
      )}
      
      <div className="w-full flex flex-col pr-1 h-[calc(100vh-120px)]">
        <div className="flex-1 overflow-hidden">
          <ScrollArea ref={scrollAreaRef} className="h-full">
            <div className="p-6 space-y-4 text-sm pb-8">
              {hasTranscriptions ? (
                displayTranscriptions.map((item, index) => (
                  <div key={generateUniqueKey(item, index)} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`${getSpeakerColor(item.speaker)} text-white text-xs px-2 py-1 rounded`}>
                        {item.speaker}
                      </span>
                      <span className="text-xs text-gray-500">{item.timestamp}</span>
                    </div>
                    <p className="text-black text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-gray-500 text-sm">{t('no_transcription_available')}</p>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
